import { useCallback, useEffect, useRef, useState } from 'react';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { LanguageDescription } from '@codemirror/language';
import { languages } from '@codemirror/language-data';
import { MergeView as CodeMirrorMergeView, unifiedMergeView } from '@codemirror/merge';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import { comparisonBaseExtensions } from './editor-theme';

/**
 * Conflict merge view: ALWAYS compares
 * "my buffer" (left, editable) against the current backend content (right,
 * read-only) — not a general diff browser. Side-by-side via
 * `@codemirror/merge`'s MergeView, collapsing to its unified form on narrow
 * panes; themed identically to {@link TextEditorView} (shared
 * `editor-theme`). The whole module rides the pane's React.lazy chunk, so
 * `@codemirror/merge` is only loaded when a conflict is actually reviewed.
 *
 * Resolutions:
 *   - Reload (take theirs)  → host `reload`: buffer = theirs, clean.
 *   - Keep mine & save      → host `keep-mine`: SAVES IMMEDIATELY with
 *                             expected_version = the conflict's version
 *                             (ui ADR-003 — the click IS the save).
 *   - Use merged            → the user edits the editable side (per-chunk
 *                             revert controls pull their chunks in); the
 *                             merged text becomes the DIRTY buffer via the
 *                             host's `merged` resolution.
 */
export interface MergeViewProps {
  /** Workspace-relative path — drives language selection + the header. */
  filename: string;
  /** MY buffer — the editable side the user merges INTO. */
  mine: string;
  /** Current backend content ("theirs") — the read-only side. */
  theirs: string;
  /** True while the host save is in flight (disables keep-mine). */
  saving?: boolean;
  onReload: () => void;
  onKeepMine: () => void;
  onUseMerged: (text: string) => void;
  /** Back to the editor without resolving — the conflict state persists. */
  onClose: () => void;
  className?: string;
}

// Below this pane width the side-by-side layout is unreadable — switch to the
// unified (single-document) merge presentation.
const NARROW_PX = 680;

export function MergeView({
  filename,
  mine,
  theirs,
  saving = false,
  onReload,
  onKeepMine,
  onUseMerged,
  onClose,
  className,
}: MergeViewProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  // The live view — either the two-pane MergeView or a unified EditorView.
  const viewRef = useRef<CodeMirrorMergeView | EditorView | null>(null);
  // The user's in-progress merged text, carried across split⇄unified
  // rebuilds (a resize must never discard manual merge work).
  const mineRef = useRef(mine);
  // Language support is resolved once and injected into every (re)build via
  // compartments on the editable side(s).
  const languageCompartment = useRef(new Compartment());
  const [langError, setLangError] = useState<string | null>(null);

  // null until the first measure — building at the wrong width and instantly
  // rebuilding would drop CM6 state for nothing.
  const [narrow, setNarrow] = useState<boolean | null>(null);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setNarrow(w < NARROW_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Current merged text from whichever presentation is live. */
  const readMerged = useCallback((): string => {
    const v = viewRef.current;
    if (v instanceof EditorView) return v.state.doc.toString();
    if (v instanceof CodeMirrorMergeView) return v.a.state.doc.toString();
    return mineRef.current;
  }, []);

  // (Re)build the merge presentation when the layout mode settles/changes.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || narrow === null) return;
    const doc = mineRef.current;
    if (narrow) {
      // Unified: ONE editable document (mine) with theirs as the original —
      // per-chunk accept/reject controls perform the merge in place.
      const view = new EditorView({
        parent: host,
        state: EditorState.create({
          doc,
          extensions: [
            ...comparisonBaseExtensions(),
            languageCompartment.current.of([]),
            unifiedMergeView({ original: theirs, mergeControls: true }),
          ],
        }),
      });
      viewRef.current = view;
    } else {
      // Side-by-side: a = mine (editable), b = theirs (read-only);
      // revertControls copy their chunks INTO mine — that is the merge.
      const view = new CodeMirrorMergeView({
        parent: host,
        a: {
          doc,
          extensions: [...comparisonBaseExtensions(), languageCompartment.current.of([])],
        },
        b: {
          doc: theirs,
          extensions: [
            ...comparisonBaseExtensions(),
            EditorState.readOnly.of(true),
            EditorView.editable.of(false),
          ],
        },
        revertControls: 'b-to-a',
        highlightChanges: true,
        gutter: true,
      });
      viewRef.current = view;
    }
    return () => {
      // Preserve the in-progress merge across the rebuild.
      mineRef.current = readMerged();
      viewRef.current?.destroy();
      viewRef.current = null;
    };
  }, [narrow, theirs, readMerged]);

  // Resolve the language once (same registry as TextEditorView) and inject it
  // into the editable side of whichever presentation is live.
  useEffect(() => {
    setLangError(null);
    const desc = LanguageDescription.matchFilename(languages, filename.split('/').pop() ?? filename);
    if (!desc) return; // plain text — correct for .log/.env/unknown
    let cancelled = false;
    desc
      .load()
      .then((support) => {
        if (cancelled) return;
        const v = viewRef.current;
        const effects = languageCompartment.current.reconfigure(support);
        if (v instanceof EditorView) v.dispatch({ effects });
        else if (v instanceof CodeMirrorMergeView) v.a.dispatch({ effects });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLangError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
    // narrow: re-apply after a rebuild swaps the underlying view.
  }, [filename, narrow]);

  return (
    <div
      className={cn('flex h-full min-h-0 flex-col rounded-md bg-muted/30 text-sm', className)}
      data-testid="merge-view"
    >
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-1.5">
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
          {filename}
          <span className="ml-2">· your buffer (editable) vs. current file</span>
          {langError && (
            <span className="ml-2 text-destructive" role="alert">
              · syntax highlighting failed to load: {langError}
            </span>
          )}
        </span>
        <Button
          type="button"
          size="sm"
          className="h-6 px-2 text-xs"
          data-testid="merge-use-merged"
          onClick={() => onUseMerged(readMerged())}
        >
          Use merged
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-6 px-2 text-xs"
          disabled={saving}
          data-testid="merge-keep-mine"
          onClick={onKeepMine}
        >
          Keep mine &amp; save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-6 px-2 text-xs"
          data-testid="merge-take-theirs"
          onClick={onReload}
        >
          Take theirs
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          data-testid="merge-close"
          onClick={onClose}
        >
          Back to editor
        </Button>
      </div>
      <div ref={hostRef} className="min-h-0 flex-1 overflow-auto" data-testid="merge-view-body" />
    </div>
  );
}
